import { defineStore } from 'pinia'
import { RefillService, ProfileService, RatesService } from '@/api'
import { useProfileStore } from './profile'
// Services
// Models
import type { SteamInventoryResponse } from '@/services/models/response'
import type { PromoCodeResponse } from '@/api/models/PromoCodeResponse'

interface DepositState {
  depositMethod:
    | 'crypto-payments'
    | 'promo-code'
    | 'referral-code'
    | 'steam-deposit'
    | 'gift-card'
    | 'steam-wallet'
    | 're-deposit'
    | 'payop'
  selectedWalletAddress: string | null
  exchangeRates?: {
    nextUpdateAt: any
    rates: Record<string, number>
  }
  steamInventory: SteamInventoryResponse | null
  selectedSteamInstanceIds: string[]
  completion: DepositCompletion | null
}

export type DepositMethod = DepositState['depositMethod']

export interface DepositCompletion {
  source: 'crypto' | 'payop'
  amountCents: number
  bonusCents: number
  creditedCents: number
  transactionId?: string
  currency?: string
}

export const useDepositStore = defineStore('deposit', {
  state: (): DepositState => ({
    depositMethod: 'crypto-payments',
    selectedWalletAddress: null,
    exchangeRates: undefined,
    steamInventory: null,
    selectedSteamInstanceIds: [],
    completion: null,
  }),
  actions: {
    // Services
    async getWalletAddress(currency: string) {
      try {
        const response = await RefillService.getV1RefillCryptoDepositWallet(currency)

        return response
      } catch (error) {
        console.error('Error fetching wallet address:', error)
        // notify(error, 'error')
      }
    },
    async usePromoCode(code: string): Promise<PromoCodeResponse> {
      try {
        const response = await ProfileService.postV1ProfileMePromoCode({
          promoCode: code,
        })
        const profileStore = useProfileStore()
        await profileStore.fetchProfile()
        return response
      } catch (error: any) {
        console.error('Error applying promo code:', error)
        // Return error response from API if available
        if (error?.body) {
          return error.body
        }
        throw error
      }
    },
    async getCurrencyExchangeRates() {
      try {
        const response = await RatesService.getV1Rates()
        this.exchangeRates = response
      } catch (error) {
        console.error('Error fetching exchange rates:', error)
      }
    },
    // Setters
    setDepositMethod(method: DepositState['depositMethod']) {
      this.depositMethod = method
    },
    setSteamInventory(inventory: SteamInventoryResponse | null) {
      this.steamInventory = inventory
      this.selectedSteamInstanceIds = []
    },
    setSelectedSteamInstanceIds(instanceIds: string[]) {
      this.selectedSteamInstanceIds = instanceIds
    },
    toggleSteamItemSelection(instanceId: string) {
      if (this.selectedSteamInstanceIds.includes(instanceId)) {
        this.selectedSteamInstanceIds = this.selectedSteamInstanceIds.filter(
          (selectedId) => selectedId !== instanceId,
        )
        return
      }

      this.selectedSteamInstanceIds = [...this.selectedSteamInstanceIds, instanceId]
    },
    setCompletion(completion: DepositCompletion) {
      this.completion = completion
    },
    clearCompletion() {
      this.completion = null
    },
  },
})
